import { type ComponentProps } from "react";
import { useNavigate } from "react-router";
import { BackArrowIcon } from "vega-icons";

import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { PageHeader } from "@vega-core/components/Headers/PageHeader";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";
import { ClosedDealNotice } from "../ClosedDealNotice/ClosedDealNotice";

type Props = ComponentProps<typeof PageHeader> & {
  onBack?: VoidFunction;
  backButtonDataTestId?: string;
  showClosedDealNotice?: boolean;
  closedDealNoticeMessage?: string;
};

export function CoInvestmentPageHeader({
  backButtonText = m.coInvestments_allCoInvestments(),
  fundName,
  showDrawer = true,
  goBack,
  onBack,
  backButtonDataTestId = "co-investment-page-back-button",
  showClosedDealNotice = false,
  closedDealNoticeMessage,
  ...props
}: Props) {
  const navigate = useNavigate();
  const { isReadOnlyDeal, isCancelledDeal } = useCoInvestDealReadOnly();

  const header = (
    <PageHeader
      backButtonText={backButtonText}
      fundName={
        isReadOnlyDeal ? (
          <div className="flex items-center gap-2">
            {fundName}
            <Tag
              dataTestId={
                isCancelledDeal ? "deal-cancelled-tag" : "deal-closed-tag"
              }
              variant={TagVariants.Disabled}
            >
              {isCancelledDeal
                ? m.coInvestments_cancelDeal_cancelled()
                : m.coInvestments_dealClosed_tag()}
            </Tag>
          </div>
        ) : (
          fundName
        )
      }
      goBack={
        goBack ??
        (showDrawer
          ? () => navigate(CoInvestmentFullPaths.CoInvestments)
          : undefined)
      }
      showDrawer={showDrawer}
      {...props}
    />
  );

  if (!onBack && !showClosedDealNotice) {
    return header;
  }

  return (
    <div className="flex flex-col gap-4">
      {onBack ? (
        <div className="flex items-center gap-3">
          <ThemedIconButton
            dataTestId={backButtonDataTestId}
            icon={<BackArrowIcon />}
            onClick={onBack}
            size="medium"
            type="button"
          />
          <div className="flex-1">{header}</div>
        </div>
      ) : (
        header
      )}
      {showClosedDealNotice ? (
        <ClosedDealNotice message={closedDealNoticeMessage} />
      ) : null}
    </div>
  );
}
